import React from "react";
import * as Tooltip from "@radix-ui/react-tooltip";

interface CannotStopWarningProps {
  alwaysColored?: boolean;
}

function CannotStopWarning({ alwaysColored = false }: CannotStopWarningProps) {
  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={0}>
        <Tooltip.Trigger asChild>
          <span
            className={`relative inline-flex items-center gap-1 rounded border border-zinc-700/30 bg-zinc-800/30 px-1.5 py-0.5 transition-all duration-200 group-hover:border-amber-600/70 ${
              alwaysColored
                ? ""
                : "group-hover:bg-zinc-800/50 group-hover:shadow-[0_0_6px_rgba(251,191,36,0.15)]"
            }`}
          >
            {/* Small warning triangle icon */}
            <span
              className="relative flex items-center justify-center"
              style={{
                width: "24px",
                height: "22px",
                transform: "scale(0.75)",
              }}
            >
              <svg width="24" height="22" viewBox="0 0 24 22" fill="none">
                <path
                  d="M 12 3 L 21 19 L 3 19 Z"
                  className={
                    alwaysColored
                      ? "fill-amber-800/70 stroke-amber-600/90 transition-colors duration-200"
                      : "fill-zinc-700 stroke-zinc-600 transition-colors duration-200 group-hover:fill-amber-800/70 group-hover:stroke-amber-600/90"
                  }
                  strokeWidth="2"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              </svg>
              {/* Exclamation mark */}
              <span
                className="flex flex-col items-center justify-center absolute inset-0"
                style={{ paddingTop: "5px", transform: "translateY(-1px)" }}
              >
                <span
                  className={`h-[6px] w-[2px] rounded-sm transition-colors duration-200 ${
                    alwaysColored
                      ? "bg-amber-400"
                      : "bg-zinc-400 group-hover:bg-amber-400"
                  }`}
                />
                <span
                  className={`mt-[1px] h-[2px] w-[2px] rounded-full transition-colors duration-200 ${
                    alwaysColored
                      ? "bg-amber-400"
                      : "bg-zinc-400 group-hover:bg-amber-400"
                  }`}
                />
              </span>
            </span>
            <span
              className={`whitespace-nowrap text-xs font-medium transition-colors duration-200 ${
                alwaysColored
                  ? "text-amber-500"
                  : "text-zinc-500 group-hover:text-amber-500"
              }`}
            >
              No stop/start
            </span>
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="bottom"
            className="z-50 max-w-xs rounded-md bg-black px-3 py-2 text-xs text-white shadow-md"
          >
            This instance cannot be stopped or restarted.
            <br />
            If your organization runs out of credits, Brev deletes the instance
            and all data on it. Deleted data cannot be recovered.
            <Tooltip.Arrow className="fill-black" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

export default CannotStopWarning;
